/**
 * Friendly Captcha v2 helpers shared by the widget and the Form submit handler.
 * JS port of services/lib/frc.ts — same field name, classification and resolve
 * logic so the shared Netlify mailer (functions/mailer.mjs) verifies Gatsby and
 * Next.js submissions identically. Both test suites load the same sentinel
 * fixture (services/lib/frc-sentinels.json); change both files together.
 *
 * The v2 widget (@friendlycaptcha/sdk) writes its result into a hidden input
 * named `frc-captcha-response`. Before completion the field holds dot-prefixed
 * sentinel values (e.g. `.UNSTARTED`, `.SOLVING`, `.ERROR.UNREACHABLE~…`)
 * rather than a real response token, so the raw value must be classified before
 * submitting.
 */

/** Hidden-field name the Friendly Captcha v2 widget writes into. */
export const FRC_RESPONSE_FIELD = "frc-captcha-response"

/** Longest a submit waits for the widget before sending without a token. */
export const FRC_RESOLVE_TIMEOUT_MS = 10000

/** Widget states in which solving only begins after an explicit start. */
const IDLE_STATES = ["unactivated", "reset"]

/**
 * Classify the widget's hidden-field value at submit time.
 *
 * - `ready`:   a real token — forward it as `frcCaptchaSolution` for server-side
 *              verification.
 * - `pending`: the widget is still connecting or solving — wait for it
 *              (see `resolveFrcResponse`).
 * - `absent`:  no token and none coming (missing field, any `.ERROR…` value
 *              including `.ERROR.UNREACHABLE~<debug>`, `.EXPIRED`, `.DESTROYED`)
 *              — submit without one; the mailer is solution-gated and fails
 *              open until verification is made mandatory (SUP-477).
 *
 * Unknown dot-values count as `pending`; `resolveFrcResponse` bounds the wait.
 *
 * @param {string | null | undefined} value
 * @returns {"ready" | "pending" | "absent"}
 */
export function classifyFrcResponse(value) {
  const token = value ? value.trim() : ""
  if (!token) return "absent"
  if (token.startsWith(".")) {
    return token.startsWith(".ERROR") ||
      token === ".EXPIRED" ||
      token === ".DESTROYED"
      ? "absent"
      : "pending"
  }
  return "ready"
}

/**
 * Wait until the widget mounted on `element` has a final response, and return
 * it. Starts an idle widget (the focus trigger can be missed when the user
 * focused the form before the widget mounted), resets an expired one, and gives
 * up after `timeoutMs` so a submit never blocks on a widget that cannot finish.
 * Resolves `undefined` when no widget is mounted.
 *
 * @param {HTMLElement | null | undefined} element the `.frc-captcha` element
 * @param {{ timeoutMs?: number }} [options]
 * @returns {Promise<string | undefined>}
 */
export function resolveFrcResponse(
  element,
  { timeoutMs = FRC_RESOLVE_TIMEOUT_MS } = {}
) {
  const widget = element && element.frcWidget
  if (!widget) return Promise.resolve(undefined)

  if (widget.response === ".EXPIRED") widget.reset()
  if (classifyFrcResponse(widget.response) !== "pending") {
    return Promise.resolve(widget.response)
  }

  return new Promise((resolve) => {
    const startIfIdle = () => {
      if (IDLE_STATES.includes(widget.state)) widget.start()
    }
    const onStateChange = () => {
      if (classifyFrcResponse(widget.response) === "pending") startIfIdle()
      else done()
    }
    const done = () => {
      clearTimeout(timer)
      element.removeEventListener("frc:widget.statechange", onStateChange)
      resolve(widget.response)
    }
    const timer = setTimeout(done, timeoutMs)
    element.addEventListener("frc:widget.statechange", onStateChange)
    startIfIdle()
  })
}

/**
 * The `.frc-captcha` element in `form` that carries the mounted widget. A form
 * can hold two mount points — the Storyblok `captcha` slot and the fallback
 * `Form` renders — but only one of them mounts a widget.
 *
 * @param {HTMLElement | null | undefined} form
 * @returns {HTMLElement | null}
 */
export function findFrcElement(form) {
  if (!form) return null
  const elements = Array.from(form.querySelectorAll(".frc-captcha"))
  return elements.find((element) => element.frcWidget) || null
}

/**
 * Reset the widget on `element` so the next submit solves a fresh puzzle —
 * a response token is single-use, so it is spent once the mailer verified it.
 *
 * @param {HTMLElement | null | undefined} element the `.frc-captcha` element
 */
export function resetFrcWidget(element) {
  try {
    element && element.frcWidget && element.frcWidget.reset()
  } catch {
    /* destroyed widget — nothing to reset */
  }
}
